import { useTranslation } from "react-i18next"

import type { AgentPresence } from "../utils/agent-presence-utils"
import type { HostedAgentSummary } from "@/api/client/hosted-agents"

import { ArrowUpRight } from "@/components/icons"

import { AgentAvatar } from "./agent-avatar"
import { MemoryModeBadge } from "./memory-mode-badge"
import { agentInitials, avatarForegroundColor } from "../utils/agent-avatar-utils"
import { agentIntroduction } from "../utils/agent-workspace-utils"

type AgentCardProps = {
  agent: HostedAgentSummary
  onOpen: (agent: HostedAgentSummary) => void
  presence?: AgentPresence
}

/** Roster card: avatar + name + title + mission description + memory-mode chip. */
export function AgentCard({ agent, onOpen, presence = "idle" }: AgentCardProps) {
  const { t } = useTranslation()

  return (
    <button
      aria-label={t("agents.roster.open_agent", { name: agent.profile.name })}
      className="card-interactive group bg-card text-card-foreground focus-visible:ring-ring flex min-h-10 flex-col overflow-hidden rounded-lg border text-left shadow-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
      data-testid="agent-card"
      type="button"
      onClick={() => onOpen(agent)}
    >
      <div className="bg-muted/30 relative flex w-full items-center justify-center border-b px-6 py-6">
        <span
          aria-hidden="true"
          className="absolute top-4 left-4 flex size-8 items-center justify-center rounded-full text-[11px] font-bold"
          style={{
            backgroundColor: agent.profile.avatar.color,
            color: avatarForegroundColor(agent.profile.avatar.color),
          }}
        >
          {agentInitials(agent)}
        </span>
        <AgentAvatar agent={agent} className="size-32" presence={presence} />
        <ArrowUpRight
          aria-hidden="true"
          className="text-muted-foreground absolute top-4 right-4 size-4"
        />
      </div>
      <div className="flex w-full flex-1 flex-col gap-3 p-5">
        <div className="min-w-0">
          <h4 className="font-KHTeka-Regular text-xl leading-tight break-words">
            {agent.profile.name}
          </h4>
          {agent.profile.title && (
            <p className="text-muted-foreground mt-0.5 truncate text-sm">{agent.profile.title}</p>
          )}
        </div>
        {presence === "working" && (
          <p className="flex items-center gap-2 text-xs font-semibold">
            <span aria-hidden="true" className="bg-foreground size-1.5 shrink-0 rounded-full" />
            {t("agents.roster.working_in_your_thread")}
          </p>
        )}
        {agentIntroduction(agent) && (
          <p className="text-muted-foreground line-clamp-3 text-sm">{agentIntroduction(agent)}</p>
        )}
        <div className="mt-auto flex flex-wrap items-center justify-between gap-3 pt-3">
          <MemoryModeBadge mode={agent.memory_mode} />
          <span className="text-sm font-semibold">{t("agents.roster.open_workspace")}</span>
        </div>
      </div>
    </button>
  )
}
